import React, { useEffect, useState } from "react";
import { api, TIMEFRAMES } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, TrendingUp, TrendingDown, RefreshCw, Zap, AlertTriangle, DollarSign } from "lucide-react";

const POPULAR = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD", "SPX500", "NAS100"];

export default function Execution() {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState(null);
  const [fills, setFills] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [killSwitch, setKillSwitch] = useState(null);
  const [symbol, setSymbol] = useState("EURUSD");
  const [side, setSide] = useState("BUY");
  const [qty, setQty] = useState(10000);
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [acc, pos, f, c, ks] = await Promise.all([
        api.brokerAccount(), api.brokerPositions(),
        api.brokerFills(20), api.brokerClosedTrades(20), api.killSwitch(),
      ]);
      setAccount(acc); setPositions(pos); setFills(f.fills || []);
      setClosedTrades(c.trades || []); setKillSwitch(ks);
    } catch (e) { toast.error("Erreur chargement compte"); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => { const id = setInterval(refresh, 15000); return () => clearInterval(id); }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const order = {
        symbol, side, quantity: parseFloat(qty),
        sl: sl ? parseFloat(sl) : null, tp: tp ? parseFloat(tp) : null,
      };
      const res = await api.submitOrder(order);
      if (res.status === "FILLED") {
        toast.success(`Ordre exécuté @ ${res.fill_price} (slip ${res.costs?.total_bps} bps, latence ${res.latency_ms}ms)`);
      } else if (res.status === "REJECTED") {
        const msg = res.risk_check?.violations?.[0]?.msg || res.reason;
        toast.error(`Rejet : ${msg}`);
      }
      refresh();
    } catch (e) { toast.error("Erreur ordre"); }
    finally { setSubmitting(false); }
  };

  const reset = async () => {
    if (!window.confirm("Réinitialiser le compte à $100 000 ?")) return;
    await api.brokerReset(100000);
    toast.success("Compte réinitialisé");
    refresh();
  };

  const equity = positions?.equity ?? account?.equity ?? 100000;
  const retPct = positions?.return_pct ?? 0;

  return (
    <div className="p-6 space-y-6" data-testid="execution-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Exécution & Paper Trading</h1>
          <p className="text-white/50 text-sm mt-1">Broker simulé avec slippage, latence et impact marché réalistes.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn" onClick={refresh} disabled={loading} data-testid="refresh-account-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rafraîchir
          </button>
          <button className="btn" onClick={reset} data-testid="reset-account-btn">Réinitialiser</button>
        </div>
      </div>

      {/* Kill Switch banner */}
      {killSwitch && killSwitch.status !== "ACTIVE" && (
        <div className={`panel p-4 flex items-start gap-3 ${killSwitch.status === "HALTED" ? "border-red-500/50" : "border-warn/50"}`} data-testid="kill-switch-banner">
          <AlertTriangle className={killSwitch.status === "HALTED" ? "text-red-500" : "text-warn"} size={20} />
          <div>
            <div className="font-semibold">{killSwitch.status === "HALTED" ? "🛑 TRADING SUSPENDU" : "⚠️ Avertissement"}</div>
            <div className="text-sm text-white/70 mt-1">{killSwitch.trigger}</div>
            <div className="text-xs text-white/40 mt-2">DD jour : {killSwitch.daily_dd_pct}% · DD total : {killSwitch.total_dd_pct}% · Pertes consécutives : {killSwitch.consecutive_losses}</div>
          </div>
        </div>
      )}

      {/* Account summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Equity" value={`$${equity.toLocaleString(undefined,{maximumFractionDigits:2})}`} testid="equity-stat" />
        <StatCard label="Return" value={`${retPct >= 0 ? "+" : ""}${retPct}%`} color={retPct >= 0 ? "up" : "down"} testid="return-stat" />
        <StatCard label="Positions ouvertes" value={positions?.n_positions ?? 0} testid="positions-count" />
        <StatCard label="Trades fermés" value={account?.n_closed_trades ?? 0} testid="closed-trades-count" />
      </div>

      {/* Order form */}
      <div className="panel p-5">
        <div className="panel-title mb-4"><Zap size={14} className="inline mr-2" />Nouvel ordre</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <label className="label">Symbole</label>
            <select className="field w-full" value={symbol} onChange={(e) => setSymbol(e.target.value)} data-testid="order-symbol">
              {POPULAR.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Côté</label>
            <select className="field w-full" value={side} onChange={(e) => setSide(e.target.value)} data-testid="order-side">
              <option value="BUY">ACHAT</option>
              <option value="SELL">VENTE</option>
            </select>
          </div>
          <div>
            <label className="label">Quantité</label>
            <input className="field w-full" type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="order-qty" />
          </div>
          <div>
            <label className="label">Stop Loss</label>
            <input className="field w-full" type="number" step="0.00001" value={sl} onChange={(e) => setSl(e.target.value)} placeholder="optionnel" data-testid="order-sl" />
          </div>
          <div>
            <label className="label">Take Profit</label>
            <input className="field w-full" type="number" step="0.00001" value={tp} onChange={(e) => setTp(e.target.value)} placeholder="optionnel" data-testid="order-tp" />
          </div>
          <button className="btn btn-primary" onClick={submit} disabled={submitting || killSwitch?.status === "HALTED"} data-testid="submit-order-btn">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : side === "BUY" ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {side === "BUY" ? "Acheter" : "Vendre"}
          </button>
        </div>
      </div>

      {/* Open positions */}
      <div className="panel p-5">
        <div className="panel-title mb-3">Positions ouvertes ({positions?.n_positions ?? 0})</div>
        {(positions?.positions || []).length === 0 ? (
          <div className="text-white/40 text-sm">Aucune position ouverte.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="positions-table">
              <thead><tr className="text-white/50 text-xs">
                <th className="text-left py-2">Symbole</th><th className="text-left">Côté</th>
                <th className="text-right">Qté</th><th className="text-right">Entry</th>
                <th className="text-right">Prix marché</th><th className="text-right">PnL non réalisé</th>
              </tr></thead>
              <tbody>
                {(positions?.positions || []).map((p, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-2 font-mono">{p.symbol}</td>
                    <td className={p.side === "BUY" ? "text-up" : "text-down"}>{p.side}</td>
                    <td className="text-right">{p.qty?.toLocaleString()}</td>
                    <td className="text-right font-mono">{p.avg_price?.toFixed(5)}</td>
                    <td className="text-right font-mono">{p.mark_price?.toFixed(5)}</td>
                    <td className={`text-right font-mono ${p.unrealized_pnl >= 0 ? "text-up" : "text-down"}`}>${p.unrealized_pnl?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent fills */}
      <div className="panel p-5">
        <div className="panel-title mb-3">Fills récents ({fills.length})</div>
        {fills.length === 0 ? (
          <div className="text-white/40 text-sm">Aucun fill.</div>
        ) : (
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm" data-testid="fills-table">
              <thead><tr className="text-white/50 text-xs">
                <th className="text-left py-2">Symbole</th><th>Côté</th>
                <th className="text-right">Qté</th><th className="text-right">Prix demandé</th>
                <th className="text-right">Prix rempli</th><th className="text-right">Slip (bps)</th>
                <th className="text-right">Coûts</th><th className="text-right">Latence</th>
              </tr></thead>
              <tbody>
                {fills.slice().reverse().map((f, i) => (
                  <tr key={i} className="border-t border-white/5 text-xs">
                    <td className="py-1.5 font-mono">{f.symbol}</td>
                    <td className={f.side === "BUY" ? "text-up" : "text-down"}>{f.side}</td>
                    <td className="text-right">{f.fill_qty}</td>
                    <td className="text-right font-mono text-white/60">{f.requested_price?.toFixed(5)}</td>
                    <td className="text-right font-mono">{f.fill_price?.toFixed(5)}</td>
                    <td className="text-right text-warn">{f.costs?.total_bps}</td>
                    <td className="text-right">${f.costs?.total_dollar}</td>
                    <td className="text-right text-white/50">{f.latency_ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Closed trades */}
      <div className="panel p-5">
        <div className="panel-title mb-3">Trades fermés ({closedTrades.length})</div>
        {closedTrades.length === 0 ? (
          <div className="text-white/40 text-sm">Aucun trade fermé.</div>
        ) : (
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm" data-testid="closed-trades-table">
              <thead><tr className="text-white/50 text-xs">
                <th className="text-left py-2">Symbole</th><th>Côté</th>
                <th className="text-right">Qté</th><th className="text-right">Entry</th>
                <th className="text-right">Exit</th><th className="text-right">PnL</th>
              </tr></thead>
              <tbody>
                {closedTrades.slice().reverse().map((t, i) => (
                  <tr key={i} className="border-t border-white/5 text-xs">
                    <td className="py-1.5 font-mono">{t.symbol}</td>
                    <td className={t.side === "BUY" ? "text-up" : "text-down"}>{t.side}</td>
                    <td className="text-right">{t.qty?.toFixed(0)}</td>
                    <td className="text-right font-mono">{t.entry?.toFixed(5)}</td>
                    <td className="text-right font-mono">{t.exit?.toFixed(5)}</td>
                    <td className={`text-right font-mono ${t.pnl >= 0 ? "text-up" : "text-down"}`}>${t.pnl?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div className="panel p-4" data-testid={testid}>
      <div className="text-white/50 text-xs uppercase tracking-widest">{label}</div>
      <div className={`font-heading text-2xl mt-2 font-bold ${color === "up" ? "text-up" : color === "down" ? "text-down" : ""}`}>{value}</div>
    </div>
  );
}
